import { useUpdateTaskLinks } from 'features/tasks/hooks/use-update-task-links';
import { useCallback } from 'react';
import { useUpdateHabitGoalTaskOrderMutation } from 'shared/hooks/queries/use-update-habit-goal-task-order-mutation';
import { SectionItemMove } from 'shared/hooks/use-droppable-section-controls';
import { Habit } from 'shared/types/habit';
import { goalInboxID, SectionBase } from 'shared/types/orderable-section';

// A habit under several goals renders in each section; only the picked-up link moves.
export const useGoalHabitReorder = () => {
  const { mutate: updateGoalSorting } = useUpdateHabitGoalTaskOrderMutation();
  const updateTaskLinks = useUpdateTaskLinks();

  return useCallback(
    (
      newSections: SectionBase<Habit>[],
      movedItem: Habit,
      { sourceSectionId, targetSectionId }: SectionItemMove,
    ) => {
      const targetSection = newSections.find(
        ({ id }) => id === targetSectionId,
      );
      if (!targetSection) {
        return;
      }

      const goalsToUpdate = [
        {
          goalId: targetSection.id,
          taskIds: targetSection.items.map(({ id }) => id),
        },
      ];

      if (sourceSectionId !== targetSectionId) {
        const sourceSection = newSections.find(
          ({ id }) => id === sourceSectionId,
        );
        if (sourceSection) {
          goalsToUpdate.push({
            goalId: sourceSection.id,
            taskIds: sourceSection.items.map(({ id }) => id),
          });
        }

        const targetGoalId =
          targetSectionId === goalInboxID ? null : targetSectionId;
        const goalIds = Array.from(
          new Set([
            ...movedItem.goalIds.filter((id) => id !== sourceSectionId),
            ...(targetGoalId ? [targetGoalId] : []),
          ]),
        );

        updateTaskLinks([
          { task: movedItem, update: { id: movedItem.id, goalIds } },
        ]);
      }

      updateGoalSorting(goalsToUpdate);
    },
    [updateGoalSorting, updateTaskLinks],
  );
};
